import { Http, Headers } from '@angular/http';
import { Injectable } from '@angular/core';
import { CONFIG } from 'app/config/config';
import { Application } from './application.model';
import { AWSSettings } from './aws_settings.model';
import { Session } from 'app/shared/services/session/session.service';
import { ImageUploadService } from 'app/shared/services/image-upload/image-upload.service';
import 'rxjs/add/operator/toPromise';
import { AnalyticsService } from 'app/shared/services/analytics/analytics.service';

@Injectable()
export class ApplicationService {
  featureHash = {
    'pages' : 'App Content',
    'events' : 'Calendar',
    'pods' : 'Home Screen Pods',
    'nav-items' : 'Navigation Items',
    'promos' : 'Promotions',
    'ads' : 'Promotions',
    'push-notifications' : 'Push Notifications',
    'training' : 'Training'
  };
  private _currentApp: Application;
  private _currentAppPromise: Promise<Application>;


  _imageUploadService: ImageUploadService;

  constructor(private _http: Http,
              private _session: Session,
              private _analytics: AnalyticsService) {
    this._imageUploadService = new ImageUploadService(_http, _session);
  }

  refreshApplication(app) {
    this._currentApp = null;
    return this.getApplicationDetails(app.sys_name);
  }

  get defaultFeatures() {
    return [
      { display_name: 'Home Screen Pods', system_name: 'pods',  icon: 'oi oi-menu', selected: false },
      { display_name: 'Promotions', system_name: 'ads',  icon: 'oi oi-bookmark', selected: false },
      { display_name: 'Push Notifications', system_name: 'push-notifications', icon: 'oi oi-bell', selected: false },
      { display_name: 'Training', system_name: 'training', icon: 'oi oi-book', selected: false },
      // { display_name: 'Messages', system_name: 'messages', icon: 'oi oi-chat', selected: false },
      { display_name: 'Calendar', system_name: 'events', icon: 'oi oi-calendar', selected: false },
      // { display_name: 'iBeacons', system_name: 'ibeacons', icon: 'oi oi-location', selected: false }
    ];
  }

  setApplication(app: Application) {
    this._currentApp = null;
    this._currentApp = app;
  }

  getApplicationDetails(sysName: string): Promise<Application> {
    if (this._currentApp && this._currentApp.sys_name === sysName) {
       return Promise.resolve(this._currentApp);
    } else if (this._currentAppPromise) {
      return this._currentAppPromise;
    } else {
      this._currentAppPromise = this._http.get(CONFIG.ENDPOINT_URL + '/apps/' + sysName, { headers: this._session.headers() })
        .toPromise()
        .then(response => {
          this._currentApp = new Application(response.json());
          setTimeout(() => {
            this._currentAppPromise = null;
          }, 200);
          this._analytics.initializeAppAnalytics(response.json().client_web_tracking_id);
          return this._currentApp;
        });
      return this._currentAppPromise;
    }
  }

  getApplicationDashboardDetails(sysName: string): Promise<any> {
    return this._http.get(CONFIG.ENDPOINT_URL + '/apps/' + sysName + '/dashboard', { headers: this._session.headers() })
      .toPromise()
      .then(response => {
        return response.json();
      });
  }

  getDisplayNameForFeature(featureSysName: string) {
    if (!this.featureHash[featureSysName]) return null;
    return this.featureHash[featureSysName];
  }

  createApplication(application: any): Promise<Application> {
    return this._http.post(CONFIG.ENDPOINT_URL + '/apps', application, { headers: this._session.headers() })
      .map(response => new Application(response.json()))
      .toPromise();
  }

  editApplication(sysName: string, editsObj: any): Promise<any> {
    return this._http.patch(CONFIG.ENDPOINT_URL + '/apps/' + sysName, editsObj, { headers: this._session.headers() })
      .toPromise()
      .then(jsonData => {return jsonData.json()})
      .catch(error => this.handleApplicationError(error));
  }

  deleteApplication(sysName: string) {
    return this._http.delete(CONFIG.ENDPOINT_URL + '/apps/' + sysName, { headers: this._session.headers() })
      .toPromise()
      .then(jsonData => new Application(JSON.parse(jsonData.json())))
      .catch(error => this.handleApplicationError(error));
  }

  setApplicationAws(sysName: string, awsObj: AWSSettings) {
    return this._http.post(CONFIG.ENDPOINT_URL + '/apps/' + sysName + '/settings/aws', awsObj, { headers: this._session.headers() })
      .toPromise()
      .then(jsonData => new AWSSettings(jsonData.json()));
  }

  handleAuthError(error?: any) {
    return error ? Promise.reject(error) : Promise.reject(new Error('Invalid login credentials.'));
  }

  handleApplicationError(error?: any) {
    return error ? Promise.reject(error) : Promise.reject(new Error('Invalid application.'));
  }

  getAllApps() : Promise<any[]> {
    return this._http.get(CONFIG.ENDPOINT_URL + '/apps', {headers: this._session.headers()})
    .toPromise()
    .then((response) => {
      return response.json();
    })
    .catch(e => console.log(e.json()))
  }

  getModifiedFeatures(sysName: string) {
    return this._http.get(CONFIG.ENDPOINT_URL + '/apps/' + sysName + '/firebase/changes', {headers: this._session.headers()})
    .map(response => {
      let features = {
        'pods': 'Home Screen', 'nav-items': 'Navigation Menu', 'events': 'Calendar', 'pages': 'App Content', 'ads': 'Promotions'
      }
      let modifiedFeatures = [];
      let res = response.json();
      if (res.features.length) {
        res.features.forEach(feature => {
          if (features[feature]) modifiedFeatures.push(features[feature]);
          else modifiedFeatures.push(feature);
        })
      }
      return modifiedFeatures;
    })
    .toPromise();
  }

  getVersionHistory(sysName: string) {
    return this._http.get(CONFIG.ENDPOINT_URL + '/apps/' + sysName + '/firebase/backups', {headers: this._session.headers()})
    .toPromise()
    .then((response) => {
      return response.json();
    })
    .catch((error) => {return error.json()})
  }

  revertToBackup(sysName: string, backupId: number) {
    let body = {backup : backupId}
    return this._http.post(CONFIG.ENDPOINT_URL + '/apps/' + sysName + '/firebase/revert', body, {headers: this._session.headers()})
    .toPromise()
    .then((response) => {
      return response.json();
    })
    .catch((error) => {return error.json()})
  }

  resetStaging(sysName: string) {
    return this._http.post(CONFIG.ENDPOINT_URL + '/apps/' + sysName + '/firebase/reset', {}, {headers: this._session.headers()})
    .toPromise()
    .then((response) => {
      return response.json();
    })
    .catch((error) => {return error.json()})
  }

  publishFeatures(sysName: string) {
    return this._http.post(CONFIG.ENDPOINT_URL + '/apps/' + sysName + '/firebase/publish', null, {headers: this._session.headers()})
    .toPromise()
    .then((response) => {
      return response.json();
    })
    .catch((e) => {return e.json()})
  }

  setApplicationMobileSettings(sysName: string, body: any) {
    return this._http.put(CONFIG.ENDPOINT_URL + '/apps/' + sysName + '/settings', body, {headers: this._session.headers()})
      .toPromise()
      .then((response) => {
        return response.json();
      })
      .catch((e) => {return e.json()})
  }

  getApplicationMobileSettings(sysName: string) : Promise<any> {
    return this._http.get(CONFIG.ENDPOINT_URL + '/apps/' + sysName + '/settings', {headers: this._session.headers()})
    .toPromise()
    .then((response) => {
      return response.json();
    })
    .catch((error) => {return error.json()});
  }

  getAppFeatures(includeStandardFeatures?: boolean) {
    let appFeats = [
    { display_name: 'Home Screen Pods', system_name: 'pods',  icon: 'oi oi-menu', selected: false },
    { display_name: 'Promotions', system_name: 'ads',  icon: 'oi oi-bookmark', selected: false },
    { display_name: 'Push Notifications', system_name: 'push-notifications', icon: 'oi oi-bell', selected: false },
    { display_name: 'Training', system_name: 'training', icon: 'oi oi-book', selected: false },
    // { display_name: 'Messages', system_name: 'messages', icon: 'oi oi-chat', selected: false },
    { display_name: 'Calendar', system_name: 'events', icon: 'oi oi-calendar', selected: false },
    // { display_name: 'iBeacons', system_name: 'ibeacons', icon: 'oi oi-location', selected: false }
  ];
  appFeats = appFeats.filter(feature => this._currentApp.hasFeature(feature.system_name));
    if(includeStandardFeatures) {
      appFeats.push({ display_name: 'App Content', system_name: 'pages', icon: 'oi oi-document', selected: false });
      appFeats.push({ display_name: 'Navigation', system_name: 'nav-items', icon: 'oi oi-menu', selected: false });
    }
    return appFeats;
  }
}



// WEBPACK FOOTER //
// /app/src/app/shared/services/app/application/application.service.ts